import {
  ComponentProps,
  FC,
  ForwardRefRenderFunction,
  forwardRef,
} from 'react';
import Datepicker from 'react-datepicker';
import type dayjs from 'utils/dayjs';
import moment from 'utils/dayjs';
import useIsMobile from '../../../hooks/useIsMobile';
import { dateFormatForUI, formatDate } from '../../../utils/date';

// components
import TextboxBase from '../textbox';

const Textbox = TextboxBase as unknown as FC<
  Partial<ComponentProps<typeof TextboxBase>>
>;

interface Props {
  // Anything dayjs can read: the search state holds an API-formatted string,
  // the booking form holds a Date, and both are passed straight through to
  // `moment(value)`/`formatDate(value)` below.
  value?: dayjs.ConfigType;
  onChange?: (date: Date | null) => void;
  name?: string;
  placeholder?: string;
  dateFormat?: string;
  // Also handed to `moment(...)`, so the same range of inputs as `value`.
  minDate?: dayjs.ConfigType;
  dataCy?: string;
  meta?: { error?: string };
  note?: string;
  className?: string;
  wrapperClassName?: string;
  calendarClassName?: string;
}

const DatepickerField: ForwardRefRenderFunction<Datepicker, Props> = (
  {
    value,
    onChange,
    name = 'bookingDate',
    placeholder = 'Select date',
    dateFormat = dateFormatForUI,
    // Omitted/undefined = today: the search and booking pickers must not
    // offer past dates. Pass null for no minimum (the dashboard filters).
    minDate = moment(),
    dataCy = 'datePickerField',
    meta: { error } = {},
    note,
    className,
    wrapperClassName = 'datepicker-field',
    calendarClassName = 'datepicker-field__popup',
    ...props
  },
  forwardedRef
) => {
  const isMobileView = useIsMobile();

  const textbox = (
    <Textbox
      label={placeholder}
      showDropdownArrow
      readonly
      dataCy={dataCy}
      error={!!error}
      errorMsg={error}
      note={note}
      className={className}
    />
  );

  return (
    <Datepicker
      ref={forwardedRef}
      wrapperClassName={wrapperClassName}
      calendarClassName={calendarClassName}
      showPopperArrow={false}
      customInput={textbox}
      enableTabLoop={false}
      {...props}
      name={name}
      onChange={onChange}
      placeholderText={placeholder}
      value={value ? formatDate(value, dateFormat) : undefined}
      selected={value ? moment(value).toDate() : undefined}
      minDate={minDate ? moment(minDate).toDate() : undefined}
      withPortal={isMobileView}
    />
  );
};

export default forwardRef(DatepickerField);
